import { ChartSymbol } from '../context/chart/chartTypes';
import { LockUpType } from '../context/pool/poolTypes';
import {
    EnabledChains,
    EnabledTokenPairs,
    Socials,
    SupportedChains,
    Tokens,
} from './constants';

const enabledChains: EnabledChains = [SupportedChains.ARBITRUM_ONE];

const defaultMetamaskChain = window?.ethereum?.networkVersion as
    | EnabledChains[number]
    | null;
const defaultChain =
    defaultMetamaskChain && enabledChains.includes(defaultMetamaskChain)
        ? defaultMetamaskChain
        : SupportedChains.ARBITRUM_ONE; // default fallback

export const config: Config = {
    defaultChain,
    enabledChains,
    minLeverage: 1.1,
    slippage: {
        min: 0.01,
        max: 10,
        default: process.env.REACT_APP_ORDERBOOK_SLIPPAGE
            ? Number(process.env.REACT_APP_ORDERBOOK_SLIPPAGE)
            : 0.1,
    },
    priceFeedDecimal: process.env.REACT_APP_PRICEFEED_DECIMAL
        ? Number(process.env.REACT_APP_PRICEFEED_DECIMAL)
        : 24,
    orderBookDecimal: process.env.REACT_APP_ORDERBOOK_DECIMAL
        ? Number(process.env.REACT_APP_ORDERBOOK_DECIMAL)
        : 24,
    chartSymbols: [
        'BTCUSDC',
        'ETHUSDC',
        'LINKUSDC',
        'BNBUSDC',
        'XRPUSDC',
        'ADAUSDC',
        'MATICUSDC',
        'DOTUSDC',
        'ARBUSDC',
        'DOGEUSDC',
        'UNIUSDC',
    ],
    areaChartTimeframes: ['1d', '1w', '1m', '1y'],
    leverageMultiplier: 1_000_000,
    lockUpOptions: [
        {
            lockUpId: undefined,
            duration: undefined,
            feeMultiplier: undefined,
        },
        {
            lockUpId: 0,
            duration: 30,
            feeMultiplier: 1.25,
        },
        {
            lockUpId: 1,
            duration: 60,
            feeMultiplier: 1.5,
        },
        {
            lockUpId: 2,
            duration: 90,
            feeMultiplier: 2.0,
        },
    ],
    socialMedias: {
        [Socials.MEDIUM]: 'https://medium.com/@unlimited_defi',
        [Socials.TWITTER]: 'https://twitter.com/unlimited_defi',
        [Socials.DISCORD]: 'https://discord.gg/unlimitednetwork',
        [Socials.TELEGRAM]: 'https://t.me/UnlimitedNetworkChat',
    },
    urls: {
        unlimitedReferralApi:
            process.env.REACT_APP_REFERRAL_ENDPOINT ||
            'https://referrals.unlimited.trade',
        priceFeedApi: `https://${
            process.env.REACT_APP_PRICEFEED || 'pf.unlimited.trade'
        }`,
        priceFeedWebsocket: `wss://${
            process.env.REACT_APP_PRICEFEED || 'pf.unlimited.trade'
        }`,
    },
    endpoints: {
        [SupportedChains.ARBITRUM_ONE]: {
            unlimitedSubgraph:
                process.env.REACT_APP_SUBGRAPH_ARBITRUM_ONE ||
                'https://subgraph.satsuma-prod.com/49eb322da234/solidant/mainnet.unlimited/api',
            orderBook:
                process.env.REACT_APP_ORDERBOOK_ARBITRUM_ONE ||
                'https://orderbook.unlimited.trade',
            wsProvider:
                'wss:////arb-mainnet.g.alchemy.com/v2/-PbH4F-527FLQJiMOIf8x7PVl56tGHuz',
            // process.env.NODE_ENV === 'development'
            //     ? 'wss://arb-mainnet.g.alchemy.com/v2/-PbH4F-527FLQJiMOIf8x7PVl56tGHuz'
            //     : `wss://${process.env.REACT_APP_PROVIDER_ARBITRUM_ONE}` ||
            //       'wss://arb-mainnet.g.alchemy.com/v2/-PbH4F-527FLQJiMOIf8x7PVl56tGHuz',
        },
    },
    addresses: {
        [SupportedChains.ARBITRUM_ONE]: {
            collateral: (
                process.env.REACT_APP_COLLATERAL_ARBITRUM_ONE ||
                '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8'
            ).toLowerCase(),
            userManager: (
                process.env.REACT_APP_USER_MANAGER_ARBITRUM_ONE ||
                '0x876B5190D21976a63e002f4C636d4464f4B3c185'
            ).toLowerCase(),
            tradeManager: (
                process.env.REACT_APP_TRADE_MANAGER_ARBITRUM_ONE ||
                '0x0b072245c5C8f19010AF2E178Bd311C38E435a68'
            ).toLowerCase(),
            tradeManagerOrders: (
                process.env.REACT_APP_TRADE_MANAGER_ORDERS_ARBITRUM_ONE ||
                '0x0b072245c5C8f19010AF2E178Bd311C38E435a68'
            ).toLowerCase(),
            tradePair: {
                [Tokens.ETH]: (
                    process.env.REACT_APP_TRADE_PAIR_ETH_ARBITRUM_ONE ||
                    '0x3838b984a8c5D5e8Cd7D6880cf57dcd13644207a'
                ).toLowerCase(),
                [Tokens.BTC]: (
                    process.env.REACT_APP_TRADE_PAIR_BTC_ARBITRUM_ONE ||
                    '0x18394FBCCD11Dfe8a95eC305F57178765bd49aA6'
                ).toLowerCase(),
                [Tokens.BNB]: (
                    process.env.REACT_APP_TRADE_PAIR_BNB_ARBITRUM_ONE ||
                    '0xB969d5B886914309663155FA321206f179930a8F'
                ).toLowerCase(),
                [Tokens.XRP]: (
                    process.env.REACT_APP_TRADE_PAIR_XRP_ARBITRUM_ONE ||
                    '0x989c812876407cF5EcE51cdfA534a4Ffa3874db7'
                ).toLowerCase(),
                [Tokens.ADA]: (
                    process.env.REACT_APP_TRADE_PAIR_ADA_ARBITRUM_ONE ||
                    '0xAC4208B90986489169F1A413D3F4320CC530e0dc'
                ).toLowerCase(),
                [Tokens.MATIC]: (
                    process.env.REACT_APP_TRADE_PAIR_MATIC_ARBITRUM_ONE ||
                    '0x99061aD06aaF40368a14fb446790d7429FdD6c82'
                ).toLowerCase(),
                [Tokens.DOT]: (
                    process.env.REACT_APP_TRADE_PAIR_DOT_ARBITRUM_ONE ||
                    '0x10EBcEB16E0f3DaF79A745C7a88323a71399Dec1'
                ).toLowerCase(),
                [Tokens.ARB]: (
                    process.env.REACT_APP_TRADE_PAIR_ARB_ARBITRUM_ONE ||
                    '0xe9BA3BFB72cF4C585Ac5233F9881F25F73a3Ee24'
                ).toLowerCase(),
                [Tokens.DOGE]: (
                    process.env.REACT_APP_TRADE_PAIR_DOGE_ARBITRUM_ONE ||
                    '0x2B86687C91ae84F9513D835621eA732BF4e38d6A'
                ).toLowerCase(),
                [Tokens.UNI]: (
                    process.env.REACT_APP_TRADE_PAIR_UNI_ARBITRUM_ONE ||
                    '0x66c482e2B43d66644759b5ceCde64a7D4E044802'
                ).toLowerCase(),
                [Tokens.LINK]: (
                    process.env.REACT_APP_TRADE_PAIR_LINK_ARBITRUM_ONE ||
                    '0xa721aaE59e9b3a6111ecF920c112eA82Fe0ebF30'
                ).toLowerCase(),
            },
            tradePairHelper: (
                process.env.REACT_APP_TRADE_PAIR_HELPER_ARBITRUM_ONE ||
                '0xE7F005697A96eF1441ca41393D069f74c6731c28'
            ).toLowerCase(),
            liquidityPools: {
                blueChip: (
                    process.env.REACT_APP_BLUECHIP_POOL_ARBITRUM_ONE ||
                    '0x1eBB487B42530D511091DC6bC4359ee767ad0d16'
                ).toLowerCase(),
                altCoin: (
                    process.env.REACT_APP_ALTCOIN_POOL_ARBITRUM_ONE ||
                    '0x0155d3f48e51e108f0dfF56F6EFBeA1A870B3cE7'
                ).toLowerCase(),
                degen: (
                    process.env.REACT_APP_DEGEN_POOL_ARBITRUM_ONE ||
                    '0x20A1012B79e8F3cA3f802533c07934eF97398dA7'
                ).toLowerCase(),
            },
        },
    },
};

type Config = {
    defaultChain: EnabledChains[number];
    enabledChains: EnabledChains;
    chartSymbols: ChartSymbol[];
    slippage: {
        min: number;
        max: number;
        default: number;
    };
    priceFeedDecimal: number;
    orderBookDecimal: number;
    minLeverage: number;
    areaChartTimeframes: ('1d' | '1w' | '1m' | '1y')[];
    leverageMultiplier: number;
    lockUpOptions: LockUpType[];
    socialMedias: Partial<Record<Socials, string>>;
    urls: {
        unlimitedReferralApi: string;
        priceFeedApi: string;
        priceFeedWebsocket: string;
    };
    endpoints: Record<
        EnabledChains[number],
        {
            unlimitedSubgraph: string;
            orderBook: string;
            wsProvider: string;
        }
    >;
    addresses: Record<
        EnabledChains[number],
        {
            collateral: string;
            tradeManager: string;
            tradeManagerOrders: string;
            userManager: string;
            tradePair: Record<EnabledTokenPairs[number], string>;
            tradePairHelper: string;
            liquidityPools: {
                blueChip: string;
                altCoin: string;
                degen: string;
            };
        }
    >;
};
